import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Calendar as CalIcon, Users, Leaf } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { format } from "date-fns";
import { getEasyArrivalMeal } from "@/data/easyArrivalMeals";

export default function BookPage() {
    const { packageId: routePkgId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [packages, setPackages] = useState([]);
    const [packageId, setPackageId] = useState(routePkgId || searchParams.get("pkg") || "");
    const [guests, setGuests] = useState(6);
    const [eventDate, setEventDate] = useState(undefined);
    const [eventTime, setEventTime] = useState("");
    const [blockedSet, setBlockedSet] = useState(new Set());
    const [dateInfo, setDateInfo] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoteErr, setQuoteErr] = useState("");
    const [mealChoice, setMealChoice] = useState(searchParams.get("meal") || "");

    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        event_location: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const selectedPkg = useMemo(
        () => packages.find((p) => p.id === packageId),
        [packages, packageId],
    );

    const selectedMeal = useMemo(
        () => (packageId === "easy-arrival" ? getEasyArrivalMeal(mealChoice) : null),
        [packageId, mealChoice],
    );

    // If Easy Arrival is chosen but no meal selected, send to the meal picker
    useEffect(() => {
        if (packageId === "easy-arrival" && !mealChoice) {
            navigate("/easy-arrival/choose-meal", { replace: true });
        }
    }, [packageId, mealChoice, navigate]);

    // Load packages
    useEffect(() => {
        api.get("/packages").then((r) => {
            setPackages(r.data);
            if (!packageId && r.data.length) setPackageId(r.data[0].id);
        });
    }, []); // eslint-disable-line

    // Load blocked dates around today
    useEffect(() => {
        const today = new Date();
        const end = new Date();
        end.setMonth(end.getMonth() + 12);
        const fmt = (d) => format(d, "yyyy-MM-dd");
        api.get("/availability", { params: { start: fmt(today), end: fmt(end) } })
            .then((r) => setBlockedSet(new Set(r.data.blocked_dates)))
            .catch(() => {});
    }, []);

    // Quote whenever package, guests, or event_date change
    useEffect(() => {
        if (!packageId || !guests) return;
        setQuote(null);
        setQuoteErr("");
        const body = { package_id: packageId, guests: Number(guests) };
        if (eventDate) body.event_date = format(eventDate, "yyyy-MM-dd");
        api.post("/bookings/quote", body)
            .then((r) => setQuote(r.data))
            .catch((e) =>
                setQuoteErr(e.response?.data?.detail || "Could not compute quote"),
            );
    }, [packageId, guests, eventDate]);

    // Fetch count of existing bookings whenever date changes
    useEffect(() => {
        if (!eventDate) {
            setDateInfo(null);
            return;
        }
        const ds = format(eventDate, "yyyy-MM-dd");
        api.get("/availability/count", { params: { event_date: ds } })
            .then((r) => setDateInfo(r.data))
            .catch(() => setDateInfo(null));
    }, [eventDate]);

    const isBlocked = (date) => {
        const ds = format(date, "yyyy-MM-dd");
        return blockedSet.has(ds);
    };

    const canSubmit =
        packageId &&
        guests >= (selectedPkg?.min_guests || 6) &&
        eventDate &&
        !isBlocked(eventDate) &&
        form.customer_name &&
        form.customer_email &&
        form.customer_phone &&
        form.event_location &&
        (packageId !== "easy-arrival" || !!mealChoice) &&
        quote &&
        !quote.email_only_date;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            toast.error("Please complete all fields and select an available date.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                package_id: packageId,
                guests: Number(guests),
                event_date: format(eventDate, "yyyy-MM-dd"),
                event_time: eventTime || null,
                meal_choice: selectedMeal
                    ? `${selectedMeal.name}`
                    : null,
                ...form,
                origin_url: window.location.origin,
            };
            const { data } = await api.post("/bookings/checkout", payload);
            // No card form — redirect to invoice/pending page
            navigate(`/booking/pending/${data.booking_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit reservation.");
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="book-page" className="pt-28 lg:pt-36 pb-24 bg-sand-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <header className="mb-12 max-w-3xl">
                    <span className="overline">Reserve your date</span>
                    <h1 className="font-serif font-light text-5xl md:text-6xl text-forest-700 mt-5 leading-[1.05]">
                        Let's plan your <em className="italic">island gathering.</em>
                    </h1>
                    <p className="text-forest-700/70 leading-relaxed mt-5">
                        Choose your menu, pick an available date, and we'll send your
                        deposit invoice through Stripe. Your reservation is confirmed
                        the moment the 50% deposit is collected.
                    </p>
                </header>

                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-10"
                    data-testid="booking-form"
                >
                    {/* Left: Choices */}
                    <div className="lg:col-span-7 space-y-10">
                        {/* Easy Arrival meal pill */}
                        {selectedMeal && (
                            <section
                                className="bg-forest-700/5 border border-forest-700/30 rounded-sm p-6 flex items-start gap-4"
                                data-testid="selected-meal-banner"
                            >
                                <Leaf
                                    className="w-5 h-5 text-forest-700 mt-1 shrink-0"
                                    strokeWidth={1.5}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="overline">Your meal</div>
                                    <div className="font-serif text-2xl text-forest-700 mt-1">
                                        {selectedMeal.name}
                                    </div>
                                    <div className="text-xs text-forest-700/65 mt-1">
                                        {selectedMeal.protein}
                                        {selectedMeal.tags.length > 0 &&
                                            " · " + selectedMeal.tags.join(" · ")}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate("/easy-arrival/choose-meal")}
                                    data-testid="change-meal-btn"
                                    className="text-sm text-forest-700 underline-link whitespace-nowrap"
                                >
                                    Change meal
                                </button>
                            </section>
                        )}

                        {/* Package + guests */}
                        <section className="bg-white border border-palm-400/30 rounded-sm p-8">
                            <h2 className="font-serif text-3xl text-forest-700 mb-6">
                                1. Your menu
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs tracking-[0.18em] uppercase text-forest-400">
                                        Package
                                    </Label>
                                    <Select value={packageId} onValueChange={setPackageId}>
                                        <SelectTrigger
                                            data-testid="package-select"
                                            className="mt-2 border-palm-400/50 focus:ring-forest-700"
                                        >
                                            <SelectValue placeholder="Choose a package" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {packages.map((p) => (
                                                <SelectItem
                                                    key={p.id}
                                                    value={p.id}
                                                    data-testid={`package-option-${p.id}`}
                                                >
                                                    {p.name} —{" "}
                                                    {p.flat_price
                                                        ? fmtUSD(p.flat_price)
                                                        : `${fmtUSD(p.price_per_person)}/pp`}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label
                                        htmlFor="guests"
                                        className="text-xs tracking-[0.18em] uppercase text-forest-400"
                                    >
                                        Guests <Users className="w-3.5 h-3.5 inline ml-1" />
                                    </Label>
                                    <Input
                                        id="guests"
                                        type="number"
                                        min={selectedPkg?.min_guests || 6}
                                        value={guests}
                                        onChange={(e) => setGuests(e.target.value)}
                                        data-testid="guests-input"
                                        className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
                                    />
                                    {selectedPkg && (
                                        <p className="text-xs text-forest-700/55 mt-1">
                                            Minimum {selectedPkg.min_guests} guests
                                        </p>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Date */}
                        <section className="bg-white border border-palm-400/30 rounded-sm p-8">
                            <h2 className="font-serif text-3xl text-forest-700 mb-2">
                                2. Pick your date
                            </h2>
                            <p className="text-sm text-forest-700/60 mb-4 flex items-center gap-2">
                                <CalIcon className="w-4 h-4" strokeWidth={1.5} />
                                Greyed-out dates are unavailable.
                            </p>
                            <div className="flex flex-col md:flex-row gap-8 items-start">
                                <div className="bg-sand-50 rounded-sm border border-palm-400/20 p-2">
                                    <Calendar
                                        mode="single"
                                        selected={eventDate}
                                        onSelect={setEventDate}
                                        disabled={(d) => {
                                            // Silent minimum-lead-time window.
                                            // Do not surface the rule to users.
                                            const cutoff = new Date();
                                            cutoff.setHours(0, 0, 0, 0);
                                            cutoff.setDate(cutoff.getDate() + 4);
                                            return d < cutoff || isBlocked(d);
                                        }}
                                        data-testid="event-date-picker"
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="flex-1 space-y-4">
                                    <div>
                                        <Label className="text-xs tracking-[0.18em] uppercase text-forest-400">
                                            Selected date
                                        </Label>
                                        <div
                                            className="mt-2 font-serif text-2xl text-forest-700"
                                            data-testid="selected-date-display"
                                        >
                                            {eventDate
                                                ? format(eventDate, "EEEE, MMMM d, yyyy")
                                                : "— Choose a date"}
                                        </div>
                                        {eventDate && dateInfo && dateInfo.total > 0 && (
                                            <div
                                                className="mt-3 p-3 rounded-sm bg-sand-50 border border-forest-400/30 text-xs text-forest-700 leading-relaxed"
                                                data-testid="date-info-popover"
                                            >
                                                <div className="font-medium text-forest-700">
                                                    {dateInfo.total === 1
                                                        ? "1 booking already on this date."
                                                        : `${dateInfo.total} bookings already on this date.`}
                                                </div>
                                                <div className="text-forest-700/70 mt-1">
                                                    {dateInfo.drop_off > 0 && (
                                                        <span>
                                                            {dateInfo.drop_off} drop-off
                                                            {dateInfo.drop_off > 1 ? "s" : ""}
                                                        </span>
                                                    )}
                                                    {dateInfo.drop_off > 0 && dateInfo.in_villa > 0 && (
                                                        <span> · </span>
                                                    )}
                                                    {dateInfo.in_villa > 0 && (
                                                        <span>
                                                            {dateInfo.in_villa} on-site chef
                                                            {dateInfo.in_villa > 1 ? "s" : ""}
                                                        </span>
                                                    )}
                                                    . You'll be added alongside them.
                                                </div>
                                            </div>
                                        )}
                                        {eventDate && dateInfo && dateInfo.total === 0 && (
                                            <div
                                                className="mt-3 text-xs text-forest-400"
                                                data-testid="date-info-empty"
                                            >
                                                ✓ No other bookings on this date — it's all yours.
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label
                                            htmlFor="event-time"
                                            className="text-xs tracking-[0.18em] uppercase text-forest-400"
                                        >
                                            Event time (optional)
                                        </Label>
                                        <Input
                                            id="event-time"
                                            type="time"
                                            value={eventTime}
                                            onChange={(e) => setEventTime(e.target.value)}
                                            data-testid="event-time-input"
                                            className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Customer details */}
                        <section className="bg-white border border-palm-400/30 rounded-sm p-8">
                            <h2 className="font-serif text-3xl text-forest-700 mb-6">
                                3. Your details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {[
                                    { id: "customer_name", label: "Full name", type: "text" },
                                    { id: "customer_email", label: "Email", type: "email" },
                                    { id: "customer_phone", label: "Phone", type: "tel" },
                                    {
                                        id: "event_location",
                                        label: "Event location / villa name",
                                        type: "text",
                                    },
                                ].map((f) => (
                                    <div
                                        key={f.id}
                                        className={f.id === "event_location" ? "md:col-span-2" : ""}
                                    >
                                        <Label
                                            htmlFor={f.id}
                                            className="text-xs tracking-[0.18em] uppercase text-forest-400"
                                        >
                                            {f.label}
                                        </Label>
                                        <Input
                                            id={f.id}
                                            type={f.type}
                                            value={form[f.id]}
                                            onChange={(e) =>
                                                setForm((s) => ({ ...s, [f.id]: e.target.value }))
                                            }
                                            data-testid={`input-${f.id}`}
                                            required
                                            className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
                                        />
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <Label
                                        htmlFor="notes"
                                        className="text-xs tracking-[0.18em] uppercase text-forest-400"
                                    >
                                        Notes, dietary needs, dream menu (optional)
                                    </Label>
                                    <Textarea
                                        id="notes"
                                        rows={4}
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm((s) => ({ ...s, notes: e.target.value }))
                                        }
                                        data-testid="input-notes"
                                        className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
                                    />
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Right: Quote Summary */}
                    <aside className="lg:col-span-5">
                        <div className="bg-forest-700 text-sand-100 rounded-sm p-8 sticky top-28">
                            <span className="overline !text-palm-400">Your reservation</span>
                            <h3 className="font-serif text-3xl mt-3 leading-tight">
                                {selectedPkg?.name || "Choose a package"}
                            </h3>
                            {selectedPkg && (
                                <p className="text-sand-100/70 text-sm mt-2">
                                    {selectedPkg.category === "drop_off"
                                        ? "Drop-off delivery"
                                        : "On-site private chef"}{" "}
                                    · {guests} guests
                                </p>
                            )}
                            {selectedMeal && (
                                <p
                                    className="text-palm-400 text-sm mt-2"
                                    data-testid="quote-meal-line"
                                >
                                    Meal: {selectedMeal.name}
                                </p>
                            )}

                            {quoteErr && (
                                <p
                                    className="mt-6 text-sm text-red-200 bg-red-900/20 p-3 rounded-sm"
                                    data-testid="quote-error"
                                >
                                    {quoteErr}
                                </p>
                            )}

                            {quote && (
                                <div
                                    className="mt-8 space-y-3 text-sm"
                                    data-testid="quote-summary"
                                >
                                    <Row label="Subtotal" value={fmtUSD(quote.subtotal)} />
                                    <Row
                                        label={quote.fee_label}
                                        value={fmtUSD(quote.fee_amount)}
                                    />
                                    {quote.holiday_fee > 0 && (
                                        <Row
                                            label={quote.holiday_label || "Holiday fee"}
                                            value={fmtUSD(quote.holiday_fee)}
                                            testid="holiday-fee-row"
                                        />
                                    )}
                                    <Row label="Gratuity (20%)" value={fmtUSD(quote.gratuity)} />
                                    <Row
                                        label="Processing (5%)"
                                        value={fmtUSD(quote.processing_fee)}
                                    />
                                    <div className="pt-3 mt-3 border-t border-sand-100/15 flex items-baseline justify-between">
                                        <span className="text-sand-100/80">Total</span>
                                        <span className="font-serif text-2xl text-emerald-400">
                                            {fmtUSD(quote.total)}
                                        </span>
                                    </div>
                                    {quote.email_only_date ? (
                                        <div
                                            className="mt-6 p-4 bg-red-950/40 border border-red-400/40 rounded-sm"
                                            data-testid="email-only-banner"
                                        >
                                            <div className="overline !text-red-100 !opacity-100">
                                                Email booking required
                                            </div>
                                            <p className="text-sm text-sand-100 mt-2 leading-relaxed">
                                                {quote.holiday_label} — this date is only
                                                bookable by email. Please contact{" "}
                                                <a
                                                    href="mailto:coralbaycatering@gmail.com?subject=Holiday%20Booking%20Inquiry"
                                                    className="underline"
                                                >
                                                    coralbaycatering@gmail.com
                                                </a>{" "}
                                                or call 340-423-6611.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="mt-6 p-4 bg-sand-100/10 rounded-sm">
                                            <div className="flex items-baseline justify-between">
                                                <span className="overline !text-palm-400">
                                                    Deposit Due Today
                                                </span>
                                                <span
                                                    className="font-serif text-3xl text-sand-100"
                                                    data-testid="deposit-amount"
                                                >
                                                    {fmtUSD(quote.deposit_due)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-sand-100/60 mt-2">
                                                Balance of {fmtUSD(quote.balance_due)} due before
                                                event.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {quote?.email_only_date ? (
                                <a
                                    href={`mailto:coralbaycatering@gmail.com?subject=${encodeURIComponent(
                                        (quote?.holiday_label || "Holiday") + " Booking Inquiry",
                                    )}&body=${encodeURIComponent(
                                        `Hello,\n\nI'd like to inquire about catering on ${
                                            eventDate ? format(eventDate, "EEEE, MMMM d, yyyy") : ""
                                        }.\n\nPackage: ${selectedPkg?.name || ""}\nGuests: ${guests}\n${
                                            selectedMeal ? `Meal: ${selectedMeal.name}\n` : ""
                                        }\nThanks!`,
                                    )}`}
                                    data-testid="email-booking-btn"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-sand-100 text-forest-900 px-6 py-4 rounded-sm hover:bg-white transition-colors text-sm tracking-wide"
                                >
                                    Email us to reserve
                                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                                </a>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={!canSubmit || submitting}
                                    data-testid="submit-booking-btn"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-sand-100 text-forest-900 px-6 py-4 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
                                >
                                    {submitting ? "Submitting…" : "Submit Reservation"}
                                    {!submitting && (
                                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                                    )}
                                </button>
                            )}
                            <p className="text-xs text-sand-100/60 mt-4 leading-relaxed">
                                {quote?.email_only_date
                                    ? "Christmas Day and New Year's Eve reservations are handled personally. We'll reply within 24 hours."
                                    : "Once you submit, you'll see your invoice number and our phone. We'll take your 50% deposit by phone to confirm the date."}
                            </p>
                        </div>
                    </aside>
                </form>
            </div>
        </div>
    );
}

function Row({ label, value, testid }) {
    return (
        <div
            className="flex items-baseline justify-between text-sand-100/80"
            data-testid={testid}
        >
            <span>{label}</span>
            <span className="text-sand-100">{value}</span>
        </div>
    );
}
